import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import {
  constants,
  PreparedFeature,
  PreparedFeedbackItem,
} from '@frontend/partviewer';

import { PART_STATUSES } from 'constants/all';

import { FEATURE_LOADING_TYPES } from 'modules/common/viewer/cadViewer/constants';
import { decodeEncodedCADViewerState } from 'modules/common/viewer/cadViewer/utils';
import {
  getPartFilesByUuids,
  getActivePartFileUuid,
  getActivePartFilesRootPartFileUuid,
} from 'modules/common/viewer/data/reducer';

import type { ActionOfType } from 'store/types';

import {
  types,
  cadViewer,
  explodedViewTool,
  viewerInteractions,
  viewerPartFileQueue,
  type CadViewerAction,
} from '../actions';
import {
  featureLoading,
  bulkGeometricAnalysis,
  geometricAnalysisQueue,
} from '../geometricAnalysisData/actions';
import { getGeometricAnalysisDataForPartFile } from '../geometricAnalysisData/reducer';
import {
  partFileBulkCadData,
  types as partFileCadDataTypes,
  type PartFileCadDataAction,
} from '../partFileCadData/actions';
import {
  getPartFileCadDataExists,
  getViewerIdsForPartFileUuid,
} from '../partFileCadData/reducer';
import {
  getFocusedUuid,
  getIsBodyFocused,
  getIsViewerReadyForPartFile,
  getSelectedFeatures,
  getSelectedManufacturabilityFeedback,
} from '../reducer';

const { ENTITY_TYPES } = constants;

type CadAction<T extends CadViewerAction['type']> = ActionOfType<
  CadViewerAction,
  T
>;

export default function* root() {
  yield takeLatest(
    types.CAD_VIEWER_SET_GEOMETRIC_ENTITIES,
    watchUpdateGeometricEntities,
  );
  yield takeLatest(types.CAD_VIEWER_RECEIVE_PART_FILE, watchReceivePartFile);
  yield takeLatest(
    types.MATERIAL_STOCK_FINISH_NESTING_CREATION,
    watchFinishNestingCreation,
  );
  yield takeLatest(types.FEATURE_SELECTION_RECEIVE, watchReceiveFeatures);
  yield takeLatest(
    types.MANUFACTURABILITY_FEEDBACK_SELECTION_RECEIVE,
    watchReceiveFeedback,
  );
  yield takeLatest(types.VIEWER_STATE_SNAP_TO, watchViewerStateSnapTo);
  yield takeLatest(
    types.CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS_EXPLICIT,
    watchReceiveShowAdditionalFeedbackKeysExplicit,
  );
  yield takeLatest(
    [
      partFileCadDataTypes.FETCH_PART_FILE_CAD_DATA_SUCCESS,
      types.VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS,
    ],
    watchSynchronizeViewerState,
  );
  yield takeEvery(
    types.GEOMETRIC_PART_FILE_BULK_PREPARE,
    watchBulkPrepareGeometricFiles,
  );
}

function* watchSynchronizeViewerState(
  action:
    | CadAction<typeof types.VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS>
    | ActionOfType<
        PartFileCadDataAction,
        typeof partFileCadDataTypes.FETCH_PART_FILE_CAD_DATA_SUCCESS
      >,
) {
  /*
  Responsible for synchronizing the viewer and CAD data flows to set the part viewer to display the correct scene once they have both loaded.
  */
  try {
    const { partFileUuid } = action.payload;
    const activePartFileRootUuid: ReturnType<
      typeof getActivePartFilesRootPartFileUuid
    > = yield select(getActivePartFilesRootPartFileUuid);

    if (partFileUuid === activePartFileRootUuid) {
      // we have the active part file
      const finishedLoadingCadData: ReturnType<
        typeof getPartFileCadDataExists
      > = yield select(getPartFileCadDataExists, activePartFileRootUuid);
      const finishedLoadingInViewer: ReturnType<
        typeof getIsViewerReadyForPartFile
      > = yield select(getIsViewerReadyForPartFile, activePartFileRootUuid);
      if (finishedLoadingCadData && finishedLoadingInViewer) {
        const activePartFileUuid: ReturnType<typeof getActivePartFileUuid> =
          yield select(getActivePartFileUuid);
        // we are ready to focus on this part file if it is not already focused
        const viewerIds: string[] = yield select(
          getViewerIdsForPartFileUuid,
          activePartFileUuid,
        );
        const firstViewerId = viewerIds[0];
        const isFocused: boolean = yield firstViewerId != null &&
          select(getIsBodyFocused, firstViewerId);
        if (!isFocused) {
          yield put(
            viewerInteractions.focusOnBodies({
              viewerBodyIds: [firstViewerId],
            }),
          );
        }
      }
    }
  } catch (error) {
    console.error('Error in watchSynchronizeViewerState', error);
  }
}

// Bulk handler for when # of files is over a limit to prevent 429s
function* watchBulkPrepareGeometricFiles(
  action: CadAction<typeof types.GEOMETRIC_PART_FILE_BULK_PREPARE>,
) {
  try {
    const { partFileUuids } = action.payload;
    const partFiles: ReturnType<typeof getPartFilesByUuids> = yield select(
      getPartFilesByUuids,
      partFileUuids,
    );

    // we make the assumption that a 'null' viewer creation status means it has processed. If it is undefined, then it has not been processed.
    const allProcessed = partFiles.every(
      ({ status }) =>
        status?.processing?.viewerCreation?.status === null ||
        status?.processing?.viewerCreation?.status === PART_STATUSES.FINISHED,
    );

    if (allProcessed) {
      const usePartFileUuids = partFiles.map(
        (partFile) => partFile.assemblyParentFileUuid || partFile.uuid,
      );
      // Geometric processing has been completed.
      // Trigger flows to receive data
      yield put(
        partFileBulkCadData.receive({
          partFileUuids: usePartFileUuids,
        }),
      );
      yield put(
        bulkGeometricAnalysis.receive({
          partFileUuids: usePartFileUuids,
        }),
      );

      for (const [ind, partFile] of partFiles.entries()) {
        if (partFile.isPrimaryFile) {
          yield put(
            viewerPartFileQueue.pendingQueueAddToStart({
              partFileUuid: usePartFileUuids[ind],
            }),
          );
        } else {
          yield put(
            viewerPartFileQueue.pendingQueueAddToEnd({
              partFileUuid: usePartFileUuids[ind],
            }),
          );
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function* watchUpdateGeometricEntities(
  action: CadAction<typeof types.CAD_VIEWER_SET_GEOMETRIC_ENTITIES>,
) {
  const { payload } = action;
  const { showAdditionalKeysExplicit } = payload;
  yield all([
    put(
      cadViewer.receiveShowAdditionalKeysExplicit({
        showAdditionalKeysExplicit: showAdditionalKeysExplicit || [],
      }),
    ),
  ]);
}

function* finishUpdateSelection({
  selectedFeatures,
  selectedManufacturabilityFeedback,
}: {
  selectedFeatures: PreparedFeature[];
  selectedManufacturabilityFeedback: PreparedFeedbackItem[];
}) {
  let partFileUuid;
  let viewerBodyIds: ReturnType<typeof getViewerIdsForPartFileUuid>;

  // collect highlight
  const highlight = [];
  if (selectedManufacturabilityFeedback.length) {
    partFileUuid = selectedManufacturabilityFeedback[0].referenceUuid;
    viewerBodyIds = yield select(getViewerIdsForPartFileUuid, partFileUuid);
    // collect all bodies related to this part uuid
    for (const bodyId of viewerBodyIds) {
      for (const feedback of selectedManufacturabilityFeedback) {
        if (feedback.faces.length > 0) {
          highlight.push({
            body: bodyId,
            type: ENTITY_TYPES.FACE,
            color: feedback.exclusion ? 0xff4136 : 0xffdc00,
            indices: feedback.faces,
          });
        }
        if (feedback.edges && feedback.edges.length > 0) {
          highlight.push({
            body: bodyId,
            type: ENTITY_TYPES.EDGE,
            color: feedback.exclusion ? 0xff4136 : 0xffae00,
            indices: feedback.edges,
          });
        }
        if (feedback.flatFeature) {
          const { flatFeature } = feedback;
          const flatPartFileUuid = flatFeature.referenceUuid;
          const flatViewerBodyIds: ReturnType<
            typeof getViewerIdsForPartFileUuid
          > = yield select(getViewerIdsForPartFileUuid, flatPartFileUuid);
          for (const relatedBodyId of flatViewerBodyIds) {
            if (flatFeature.faces.length > 0) {
              highlight.push({
                body: relatedBodyId,
                type: ENTITY_TYPES.FACE,
                color: feedback.exclusion ? 0xff4136 : 0xffdc00,
                indices: flatFeature.faces,
              });
            }
            if (flatFeature.edges && flatFeature.edges.length > 0) {
              highlight.push({
                body: relatedBodyId,
                type: ENTITY_TYPES.EDGE,
                color: feedback.exclusion ? 0xff4136 : 0xffae00,
                indices: flatFeature.edges,
              });
            }
          }
        }
      }
    }
  }
  if (selectedFeatures.length) {
    partFileUuid = selectedFeatures[0].referenceUuid;
    // collect all bodies related to this part uuid
    viewerBodyIds = yield select(getViewerIdsForPartFileUuid, partFileUuid);
    for (const bodyId of viewerBodyIds) {
      for (const feature of selectedFeatures) {
        if (feature.faces.length > 0) {
          highlight.push({
            body: bodyId,
            type: ENTITY_TYPES.FACE,
            color: feature.color,
            indices: feature.faces,
          });
        }
        if (feature.edges && feature.edges.length > 0) {
          highlight.push({
            body: bodyId,
            type: ENTITY_TYPES.EDGE,
            color: feature.color,
            indices: feature.edges,
          });
        }
        if (feature.flatFeature) {
          const { flatFeature } = feature;
          const flatPartFileUuid = flatFeature.referenceUuid;
          const flatViewerBodyIds: ReturnType<
            typeof getViewerIdsForPartFileUuid
          > = yield select(getViewerIdsForPartFileUuid, flatPartFileUuid);
          for (const relatedBodyId of flatViewerBodyIds) {
            if (flatFeature.faces.length > 0) {
              highlight.push({
                body: relatedBodyId,
                type: ENTITY_TYPES.FACE,
                color: flatFeature.color,
                indices: flatFeature.faces,
              });
            }
            if (flatFeature.edges && flatFeature.edges.length > 0) {
              highlight.push({
                body: relatedBodyId,
                type: ENTITY_TYPES.EDGE,
                color: flatFeature.color,
                indices: flatFeature.edges,
              });
            }
          }
        }
      }
    }
  }

  // collect keys for additional feedback
  const selections: (PreparedFeedbackItem | PreparedFeature)[] =
    selectedManufacturabilityFeedback.concat(selectedFeatures);
  const showAdditionalKeys = [];
  for (const selection of selections) {
    if (!selection.isAdditionalContentExplicit) {
      showAdditionalKeys.push(selection.key);
      if (selection.flatFeature) {
        showAdditionalKeys.push(selection.flatFeature.key);
      }
    }
  }

  yield all([
    put(cadViewer.receiveShowAdditionalKeys({ showAdditionalKeys })),
    put(cadViewer.receiveHighlight({ highlight })),
  ]);

  // collect loading requirements for addtional feedback and dispatch request
  const feedbackNeedLoading = selectedManufacturabilityFeedback.filter(
    (f) => f.needsLoading && !f.isAdditionalContentExplicit,
  );
  if (feedbackNeedLoading.length > 0) {
    // need to collect linked flat feature as well
    const useFeatures = [];
    for (const f of feedbackNeedLoading) {
      useFeatures.push(f);
      if (
        f.flatFeature &&
        f.flatFeature.needsLoading &&
        !f.flatFeature.isAdditionalContentExplicit
      ) {
        useFeatures.push(f.flatFeature);
      }
    }
    yield put(
      featureLoading.request({
        features: useFeatures,
        partFileUuid,
        type: FEATURE_LOADING_TYPES.FEEDBACK,
      }),
    );
  }

  const featureNeedLoading = selectedFeatures.filter(
    (f) => f.needsLoading && !f.isAdditionalContentExplicit,
  );
  if (featureNeedLoading.length > 0) {
    // need to collect linked flat feature as well
    const useFeatures = [];
    for (const f of featureNeedLoading) {
      useFeatures.push(f);
      if (
        f.flatFeature &&
        f.flatFeature.needsLoading &&
        !f.flatFeature.isAdditionalContentExplicit
      ) {
        useFeatures.push(f.flatFeature);
      }
    }
    yield put(
      featureLoading.request({
        features: useFeatures,
        partFileUuid,
        type: FEATURE_LOADING_TYPES.FEATURE,
      }),
    );
  }
}

function* watchReceiveFeatures(
  action: CadAction<typeof types.FEATURE_SELECTION_RECEIVE>,
) {
  try {
    const { payload } = action;
    const { features: selectedFeatures } = payload;
    const selectedManufacturabilityFeedback: ReturnType<
      typeof getSelectedManufacturabilityFeedback
    > = yield select(getSelectedManufacturabilityFeedback);

    yield call(finishUpdateSelection, {
      selectedFeatures,
      selectedManufacturabilityFeedback,
    });
  } catch (error) {
    console.error('Error in watchReceiveFeatures', error);
  }
}

function* watchReceiveFeedback(
  action: CadAction<typeof types.MANUFACTURABILITY_FEEDBACK_SELECTION_RECEIVE>,
) {
  try {
    const { payload } = action;
    const { manufacturabilityFeedback: selectedManufacturabilityFeedback } =
      payload;
    const selectedFeatures: ReturnType<typeof getSelectedFeatures> =
      yield select(getSelectedFeatures);

    yield call(finishUpdateSelection, {
      selectedFeatures,
      selectedManufacturabilityFeedback,
    });
  } catch (error) {
    console.error('Error in watchReceiveFeedback', error);
  }
}

function* watchViewerStateSnapTo({
  payload,
}: CadAction<typeof types.VIEWER_STATE_SNAP_TO>) {
  const { cadViewerState } = payload;
  const { showAdditionalKeysExplicit } = decodeEncodedCADViewerState(
    cadViewerState || {},
  );

  // explicitly receive the showAdditionalKeysExplicit to trigger the loading saga
  if (showAdditionalKeysExplicit && showAdditionalKeysExplicit.length > 0) {
    yield put(
      cadViewer.receiveShowAdditionalKeysExplicit({
        showAdditionalKeysExplicit,
      }),
    );
  }
}

function* watchReceiveShowAdditionalFeedbackKeysExplicit({
  payload,
}: CadAction<typeof types.CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS_EXPLICIT>) {
  try {
    const { showAdditionalKeysExplicit } = payload;
    const partFileUuid: ReturnType<typeof getFocusedUuid> = yield select(
      getFocusedUuid,
    );
    const geometricAnalysisData: ReturnType<
      typeof getGeometricAnalysisDataForPartFile
    > = yield select(getGeometricAnalysisDataForPartFile, partFileUuid);

    if (!geometricAnalysisData) return;

    const nextFeatures = geometricAnalysisData.features.filter((f) =>
      showAdditionalKeysExplicit.includes(f.key),
    );

    const needLoading = nextFeatures.filter(
      (f) => f.needsLoading && f.isAdditionalContentExplicit,
    );
    if (needLoading.length > 0) {
      // need to collect linked flat feature as well
      const useFeatures = [];
      for (const f of needLoading) {
        useFeatures.push(f);
        if (
          f.flatFeature &&
          f.flatFeature.needsLoading &&
          f.flatFeature.isAdditionalContentExplicit
        ) {
          useFeatures.push(f.flatFeature);
        }
      }
      yield put(
        featureLoading.request({
          features: useFeatures,
          partFileUuid: partFileUuid,
          type: FEATURE_LOADING_TYPES.FEATURE,
        }),
      );
    }
  } catch (error) {
    console.error(
      'Error in watchReceiveShowAdditionalFeedbackKeysExplicit',
      error,
    );
  }
}

function* watchReceivePartFile(
  action: CadAction<typeof types.CAD_VIEWER_RECEIVE_PART_FILE>,
) {
  try {
    const { file, shouldFocus, shouldPropagateVisibility, ignoreQueue } =
      action.payload;

    const focusedUuid = file.uuid;

    if (shouldFocus) {
      const viewerIds: ReturnType<typeof getViewerIdsForPartFileUuid> =
        yield select(getViewerIdsForPartFileUuid, focusedUuid);
      const focusedBodies = viewerIds.length ? viewerIds : [];

      if (shouldPropagateVisibility) {
        yield all([
          put(cadViewer.receiveActiveBodies({ activeBodies: focusedBodies })),
          put(
            viewerInteractions.focusOnPartFile({ partFileUuid: focusedUuid }),
          ),
        ]);
      } else {
        yield all([
          put(cadViewer.receiveActiveBodies({ activeBodies: focusedBodies })),
        ]);
      }
    }

    // ignoreQueue used to skip adding partFileUUID to the geometricAnalysisQueue when using bulk endpoint
    if (!ignoreQueue) {
      yield put(geometricAnalysisQueue.addToEnd({ partFileUuid: focusedUuid }));
    }

    yield all([
      put(cadViewer.receiveFocusedUuid({ focusedUuid })),
      put(cadViewer.receiveNativeUnits({ nativeUnits: file.units })),
      put(cadViewer.setGeometricEntities({})),
      put(explodedViewTool.disable()),
    ]);
  } catch (error) {
    console.error('Error occured in watchRecievePartFile', error);
  }
}

function* watchFinishNestingCreation(
  action: CadAction<typeof types.MATERIAL_STOCK_FINISH_NESTING_CREATION>,
) {
  try {
    const { payload } = action;
    const { result } = payload;
    yield put(cadViewer.receiveFocusedBodies({ focusedBodies: [result.key] }));
  } catch (error) {
    console.error('Error in watchFinishNestingCreation', error);
  }
}
